'use client';

import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AddressFields } from '@sola/ark-core/molecules/address-fields/address-fields.tsx';
import {
  Dialog,
  DialogActionClose,
  DialogActionPrimary,
  DialogActions,
  DialogContent,
} from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { Form } from '@sola/ark-core/molecules/form/form.tsx';
import type { ModalProps } from '@sola/ark-core/molecules/modal/modal.js';
import { useAddressUpdate } from '@sola/my-sdk/address/hooks.ts';
import { CheckoutAddressTypeEnum } from '@sola/schema/dotcom/checkout/contactInfo.ts';
import type { MailingAddress as MailingAddressModal } from '@sola/schema/dotcom/shared/address.ts';
import { MailingAddressEnum } from '@sola/schema/dotcom/shared/address.ts';
import {
  COUNTRY_CODE_US,
  mapCountryNameToCode,
} from '@sola/schema/dotcom/shared/geo.ts';
import type { GraphQLSupporterAddress } from '@sola/schema/dotcom/supporters/schema.generated.js';

import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import {
  createAddressUpdateErrorAlert,
  createAddressUpdateSuccessAlert,
} from '../../utils/alerts.tsx';
import { mapAddressDetails } from '../../utils/map-address-Details.ts';

export const EditMailingModal = ({
  addressDetails,
  open,
  onOpenChange,
  onClose,
}: {
  addressDetails: GraphQLSupporterAddress;
  open: ModalProps['open'];
  onOpenChange: ModalProps['onOpenChange'];
  onClose: ModalProps['onClose'];
}) => {
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const editMailingAddress = useAddressUpdate();

  const defaultFormValues: MailingAddressModal = {
    [MailingAddressEnum.Address]: addressDetails?.street ?? '',
    [MailingAddressEnum.Address2]: addressDetails?.street2 ?? '',
    [MailingAddressEnum.City]: addressDetails?.city ?? '',
    [MailingAddressEnum.Country]:
      mapCountryNameToCode(addressDetails?.country) ?? '',
    [MailingAddressEnum.State]: addressDetails?.state ?? '',
    [MailingAddressEnum.Postal]: addressDetails?.postalCode ?? '',
  };

  const { t } = useTranslation(['core', 'my-settings']);

  const handleSubmit = async (data: FieldValues) => {
    clearAlerts();
    try {
      await editMailingAddress.mutateAsync({
        address: mapAddressDetails(data as MailingAddressModal),
      });

      addAlerts(createAddressUpdateSuccessAlert());
      onClose?.();
    } catch {
      addAlerts(createAddressUpdateErrorAlert());
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent icon="mail-01" title={t('my-settings:form.mailing.title')}>
        <Form
          onSubmit={handleSubmit}
          options={{
            defaultValues: defaultFormValues,
          }}
        >
          <AddressFields
            addressType={CheckoutAddressTypeEnum.Mailing}
            defaultCountry={COUNTRY_CODE_US}
          />
          <DialogActions>
            <DialogActionClose>{t('core:actions.cancel')}</DialogActionClose>
            <DialogActionPrimary type="submit">
              {t('core:actions.save')}
            </DialogActionPrimary>
          </DialogActions>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
